import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Eye, UserPlus, TrendingUp, Globe, FileText, Megaphone, Copy, Check } from 'lucide-react';

const API = window.location.origin + '/api';

function fmtShortDate(iso) {
  try {
    return new Date(iso + 'T00:00:00Z').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch { return iso; }
}

function StatCard({ icon: Icon, label, value, sublabel, color = '#0a1f44', bg = '#f8fafc' }) {
  return (
    <div style={{
      background: 'white', border: '1px solid var(--border-gray)', borderRadius: 12,
      padding: '18px 20px', display: 'flex', gap: 14, alignItems: 'center'
    }}>
      <div style={{ background: bg, color, width: 48, height: 48, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon size={22} />
      </div>
      <div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontWeight: 600, letterSpacing: 0.03, textTransform: 'uppercase' }}>{label}</div>
        <div style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--navy)', lineHeight: 1.15 }}>{value}</div>
        {sublabel && <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{sublabel}</div>}
      </div>
    </div>
  );
}

export default function AdminKitAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [builder, setBuilder] = useState({ source: '', medium: '', campaign: '' });
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/merchant-kit/admin/analytics`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Could not load analytics'))
      .finally(() => setLoading(false));
  }, []);

  const builtLink = (() => {
    const base = `${window.location.origin}/merchant-kit`;
    const params = new URLSearchParams();
    if (builder.source) params.set('utm_source', builder.source.trim());
    if (builder.medium) params.set('utm_medium', builder.medium.trim());
    if (builder.campaign) params.set('utm_campaign', builder.campaign.trim());
    const q = params.toString();
    return q ? `${base}?${q}` : base;
  })();

  const copyBuilder = async () => {
    try {
      await navigator.clipboard.writeText(builtLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  const maxDaily = data?.daily?.reduce((m, d) => Math.max(m, d.visits, d.leads), 0) || 1;

  return (
    <div className="admin-page" data-testid="admin-kit-analytics-page" style={{ maxWidth: 1100, margin: '0 auto' }}>
      <button className="back-btn" onClick={() => navigate('/admin')}>
        <ArrowLeft /> Admin
      </button>
      <h2 style={{ margin: '0 0 4px' }}>Merchant Kit Analytics</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', margin: '0 0 24px' }}>
        Traffic and conversion on your public sales surface. Last 30 days.
      </p>

      {loading && <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading analytics…</div>}
      {error && <div style={{ padding: 16, background: '#fef2f2', color: '#991b1b', borderRadius: 8 }}>{error}</div>}

      {data && (
        <>
          {/* Headline stat cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginBottom: 28 }}>
            <StatCard
              icon={Eye}
              label="Visits (30d)"
              value={data.totals.visits}
              sublabel={`All-time: ${data.all_time.visits}`}
              color="#4338CA" bg="#EEF2FF"
            />
            <StatCard
              icon={UserPlus}
              label="Leads (30d)"
              value={data.totals.leads}
              sublabel={`All-time: ${data.all_time.leads}`}
              color="#065F46" bg="#D1FAE5"
            />
            <StatCard
              icon={TrendingUp}
              label="Conversion (30d)"
              value={`${data.totals.conversion_percent}%`}
              sublabel={`All-time: ${data.all_time.conversion_percent}%`}
              color="#9A3412" bg="#FFEDD5"
            />
          </div>

          {/* Daily chart */}
          <div style={{ background: 'white', border: '1px solid var(--border-gray)', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16 }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: 'var(--navy)' }}>Last 30 days</h3>
              <div style={{ display: 'flex', gap: 12, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#6366F1', borderRadius: 2, marginRight: 4 }} /> Visits</span>
                <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#10B981', borderRadius: 2, marginRight: 4 }} /> Leads</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 180 }} data-testid="analytics-chart">
              {data.daily.map(d => (
                <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, minWidth: 0 }}>
                  <div style={{ width: '100%', display: 'flex', alignItems: 'flex-end', height: 160, gap: 1 }}>
                    <div title={`${d.visits} visits`} style={{
                      flex: 1, background: '#6366F1', borderRadius: '3px 3px 0 0',
                      height: `${(d.visits / maxDaily) * 100}%`, minHeight: d.visits > 0 ? 2 : 0
                    }} />
                    <div title={`${d.leads} leads`} style={{
                      flex: 1, background: '#10B981', borderRadius: '3px 3px 0 0',
                      height: `${(d.leads / maxDaily) * 100}%`, minHeight: d.leads > 0 ? 2 : 0
                    }} />
                  </div>
                  <div style={{ fontSize: '0.62rem', color: 'var(--text-muted)', whiteSpace: 'nowrap', overflow: 'hidden' }}>
                    {fmtShortDate(d.date)}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            {/* Top referrers */}
            <div style={{ background: 'white', border: '1px solid var(--border-gray)', borderRadius: 12, padding: 20 }} data-testid="top-referrers">
              <h3 style={{ margin: '0 0 14px', fontSize: '1rem', color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Globe size={16} /> Top referrers
              </h3>
              {data.top_referrers.length === 0 && (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '20px 0' }}>
                  No traffic yet. Share your <code>/merchant-kit</code> link on WhatsApp, LinkedIn, Facebook to start.
                </div>
              )}
              {data.top_referrers.map(r => {
                const pct = Math.round((r.visits / (data.top_referrers[0]?.visits || 1)) * 100);
                return (
                  <div key={r.host} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.85rem' }}>
                      <span style={{ color: '#334155', fontWeight: 500 }}>{r.host}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{r.visits}</span>
                    </div>
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: '#6366F1' }} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Top pages */}
            <div style={{ background: 'white', border: '1px solid var(--border-gray)', borderRadius: 12, padding: 20 }} data-testid="top-pages">
              <h3 style={{ margin: '0 0 14px', fontSize: '1rem', color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={16} /> Top pages
              </h3>
              {data.top_pages.length === 0 && (
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '20px 0' }}>
                  No page views yet.
                </div>
              )}
              {data.top_pages.map(p => {
                const pct = Math.round((p.visits / (data.top_pages[0]?.visits || 1)) * 100);
                return (
                  <div key={p.path} style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: '0.85rem' }}>
                      <span style={{ color: '#334155', fontFamily: 'monospace', fontSize: '0.78rem' }}>{p.path}</span>
                      <span style={{ color: 'var(--text-muted)' }}>{p.visits}</span>
                    </div>
                    <div style={{ height: 6, background: '#f1f5f9', borderRadius: 3, overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: '#10B981' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <p style={{ marginTop: 24, color: 'var(--text-muted)', fontSize: '0.75rem', textAlign: 'center' }}>
            Pageviews are deduped per session and path. IPs are one-way hashed. No third-party analytics used.
          </p>

          {/* Campaigns table */}
          <div style={{ background: 'white', border: '1px solid var(--border-gray)', borderRadius: 12, padding: 20, marginTop: 24 }} data-testid="top-campaigns">
            <h3 style={{ margin: '0 0 6px', fontSize: '1rem', color: 'var(--navy)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Megaphone size={16} /> Top campaigns (UTM)
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.78rem', margin: '0 0 14px' }}>
              Traffic + conversion split by <code>utm_source</code> / <code>utm_campaign</code>. Build tagged links below.
            </p>
            {(!data.top_campaigns || data.top_campaigns.length === 0) && (
              <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem', padding: '10px 0' }}>
                No tagged traffic yet. Share the URL you build below.
              </div>
            )}
            {data.top_campaigns && data.top_campaigns.length > 0 && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-gray)', textAlign: 'left', color: 'var(--text-muted)' }}>
                      <th style={{ padding: '8px 12px', fontWeight: 600 }}>Source</th>
                      <th style={{ padding: '8px 12px', fontWeight: 600 }}>Campaign</th>
                      <th style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'right' }}>Visits</th>
                      <th style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'right' }}>Leads</th>
                      <th style={{ padding: '8px 12px', fontWeight: 600, textAlign: 'right' }}>Conv %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.top_campaigns.map((c, i) => (
                      <tr key={`${c.source}-${c.campaign}-${i}`} style={{ borderBottom: '1px solid #f1f5f9' }} data-testid={`campaign-row-${i}`}>
                        <td style={{ padding: '10px 12px', color: '#334155', fontWeight: 500 }}>{c.source}</td>
                        <td style={{ padding: '10px 12px', color: '#334155' }}>{c.campaign}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#334155' }}>{c.visits}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: c.leads > 0 ? '#065F46' : '#94a3b8', fontWeight: c.leads > 0 ? 600 : 400 }}>{c.leads}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: c.conversion_percent >= 5 ? '#065F46' : '#334155' }}>{c.conversion_percent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* UTM builder */}
            <div style={{ marginTop: 20, padding: 16, background: '#f8fafc', borderRadius: 8, border: '1px dashed var(--border-gray)' }} data-testid="utm-builder">
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--navy)', marginBottom: 10 }}>Build a tagged link</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 10 }}>
                <input
                  type="text" placeholder="Source (e.g. whatsapp)" value={builder.source}
                  onChange={e => setBuilder({ ...builder, source: e.target.value })}
                  data-testid="utm-source-input"
                  style={{ padding: 8, borderRadius: 6, border: '1px solid var(--border-gray)', fontSize: '0.85rem', fontFamily: 'inherit' }}
                />
                <input
                  type="text" placeholder="Medium (e.g. dm, post)" value={builder.medium}
                  onChange={e => setBuilder({ ...builder, medium: e.target.value })}
                  data-testid="utm-medium-input"
                  style={{ padding: 8, borderRadius: 6, border: '1px solid var(--border-gray)', fontSize: '0.85rem', fontFamily: 'inherit' }}
                />
                <input
                  type="text" placeholder="Campaign (e.g. jul-outreach)" value={builder.campaign}
                  onChange={e => setBuilder({ ...builder, campaign: e.target.value })}
                  data-testid="utm-campaign-input"
                  style={{ padding: 8, borderRadius: 6, border: '1px solid var(--border-gray)', fontSize: '0.85rem', fontFamily: 'inherit' }}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <code data-testid="utm-built-link" style={{ flex: 1, background: 'white', padding: '8px 12px', borderRadius: 6, border: '1px solid var(--border-gray)', fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {builtLink}
                </code>
                <button
                  onClick={copyBuilder}
                  data-testid="utm-copy-btn"
                  style={{ padding: '8px 14px', background: 'var(--navy)', color: 'white', border: 'none', borderRadius: 6, fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
